/**
 * `/admin/metrics` — per-user token consumption (24h or 7d).
 */

import { useEffect, useState } from 'react';
import type { AdminTokenUsageResponse } from '@eloquor/api-contract';
import { metricsTokens } from '../../api/admin';

export function AdminMetricsPage() {
  const [range, setRange] = useState<'24h' | '7d'>('24h');
  const [data, setData] = useState<AdminTokenUsageResponse | null>(null);

  useEffect(() => {
    void metricsTokens(range).then(setData);
  }, [range]);

  const sorted = data ? [...data.rows].sort((a, b) => b.totalTokens - a.totalTokens) : [];
  const total = sorted.reduce((acc, r) => acc + r.totalTokens, 0);

  return (
    <>
      <div className="auth-eyebrow"><span className="num">ADMIN</span> · Token metrics</div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        Token usage by user
      </h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={range === '24h' ? 'btn-primary' : 'btn-ghost'}
          style={{ width: 'auto', margin: 0, padding: '6px 12px', fontSize: 12 }}
          onClick={() => setRange('24h')}
        >
          Last 24h
        </button>
        <button
          className={range === '7d' ? 'btn-primary' : 'btn-ghost'}
          style={{ width: 'auto', margin: 0, padding: '6px 12px', fontSize: 12 }}
          onClick={() => setRange('7d')}
        >
          Last 7 days
        </button>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)' }}>
          Total: {total.toLocaleString()} tokens
        </span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <th style={{ padding: '8px 12px' }}>User</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Prompt</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Completion</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Reasoning</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Calls</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.userId} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '10px 12px' }}>
                <div style={{ fontWeight: 500 }}>{r.displayName}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{r.email}</div>
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                {r.totalTokens.toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                {r.promptTokens.toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                {r.completionTokens.toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                {r.reasoningTokens.toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                {r.calls}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
